import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowRight } from "lucide-react";

const LINKS = [
  { label: "How It Works", href: "#how-it-works", testid: "nav-link-how-it-works" },
  { label: "Services", href: "#services", testid: "nav-link-services" },
  { label: "Become a Tasker", href: "#become-a-tasker", testid: "nav-link-become-tasker" },
  { label: "Safety", href: "#safety", testid: "nav-link-safety" },
  { label: "Help", href: "#help", testid: "nav-link-help" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,box-shadow,border-color] duration-300 border-b ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl border-slate-200/80 shadow-[0_8px_30px_-12px_rgba(10,25,47,0.12)]"
          : "bg-transparent border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-[72px] flex items-center justify-between">
        <a href="#top" data-testid="nav-logo" className="flex items-center group">
          <img
            src="/brand/logo-full.png"
            alt="Your Helper"
            className="h-9 sm:h-10 w-auto group-hover:scale-[1.02] transition-transform duration-300"
          />
        </a>

        <nav className="hidden lg:flex items-center gap-8" data-testid="nav-desktop-links">
          {LINKS.map((l) => (
            <a
              key={l.label}
              href={l.href}
              data-testid={l.testid}
              className="nav-link text-sm font-medium text-slate-600 hover:text-navy transition-colors duration-200"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <a
            href="#app"
            data-testid="nav-login-btn"
            className="text-sm font-semibold text-navy px-4 py-2.5 rounded-full hover:bg-slate-100 transition-colors duration-200"
          >
            Log In
          </a>
          <a
            href="#app"
            data-testid="nav-get-started-btn"
            className="group inline-flex items-center gap-2 text-sm font-semibold bg-teal hover:bg-teal-hover text-navy px-5 py-2.5 rounded-full shadow-md shadow-teal/20 transition-colors duration-200"
          >
            Get Started
            <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform duration-200" />
          </a>
        </div>

        <button
          data-testid="nav-hamburger-btn"
          onClick={() => setOpen(!open)}
          className="lg:hidden w-11 h-11 grid place-items-center rounded-full bg-white border border-slate-200 text-navy"
          aria-label="Toggle menu"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden bg-white/95 backdrop-blur-xl border-t border-slate-100"
          >
            <div className="px-6 py-6 flex flex-col gap-1">
              {LINKS.map((l, i) => (
                <motion.a
                  key={l.label}
                  href={l.href}
                  data-testid={`mobile-${l.testid}`}
                  onClick={() => setOpen(false)}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i }}
                  className="font-display text-2xl font-bold text-navy py-3 border-b border-slate-100"
                >
                  {l.label}
                </motion.a>
              ))}
              <div className="flex gap-3 pt-5">
                <a
                  href="#app"
                  data-testid="mobile-nav-login-btn"
                  onClick={() => setOpen(false)}
                  className="flex-1 text-center text-sm font-semibold text-navy px-4 py-3.5 rounded-full border border-slate-300"
                >
                  Log In
                </a>
                <a
                  href="#app"
                  data-testid="mobile-nav-get-started-btn"
                  onClick={() => setOpen(false)}
                  className="flex-1 text-center text-sm font-semibold bg-teal text-navy px-4 py-3.5 rounded-full"
                >
                  Get Started
                </a>
              </div>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
